import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Ogrenciler from './pages/Ogrenciler';
import Egitmenler from './pages/Egitmenler';
import Salonlar from './pages/Salonlar';
import Dersler from './pages/Dersler';
import OgrenciProfil from './pages/OgrenciProfil';
import Yoklama from './pages/Yoklama';
import Hakedis from './pages/Hakedis';
import Program from './pages/Program';
import Muhasebe from './pages/Muhasebe';
import Kasa from './pages/Kasa';
import DersDetay from './pages/DersDetay';
import ProfilimSayfasi from './pages/ProfilimSayfasi';
import EgitmenPaneli from './pages/EgitmenPaneli';
import MesajlarSayfasi from './pages/MesajlarSayfasi';
import EgitmenDetay from './pages/EgitmenDetay';
import CRM from './pages/CRM';
import CRMAday from './pages/CRMAday';
import Etkinlikler from './pages/Etkinlikler';
import Katilim from './pages/Katilim';
import KrediPaketleri from './pages/KrediPaketleri';
import TPAyarlari from './pages/TPAyarlari';
import TPListesi from './pages/TPListesi';
import Takvim from './pages/Takvim';
import Subeler from './pages/Subeler';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import KullaniciYonetimi from './pages/KullaniciYonetimi';
import Loglar from './pages/Loglar';
import OtomatikPostalar from './pages/OtomatikPostalar';
import WhatsappAI from './pages/WhatsappAI';
import Gizlilik from './pages/Gizlilik';
import Indir from './pages/Indir';
import RiskRaporu from './pages/RiskRaporu';
import BirlestirmeTalepleri from './pages/BirlestirmeTalepleri';
import TumKullanicilar from './pages/TumKullanicilar';
import Personel from './pages/Personel';
import Arsiv from './pages/Arsiv';
import ReferansYonetim from './pages/ReferansYonetim';
import Layout from './components/Layout';
import OgrenciLayout from './components/OgrenciLayout';
import EgitmenLayout from './components/EgitmenLayout';
import DevPanel from './components/DevPanel';

const isAdmin = (rol?: string) => ['admin', 'super_admin', 'sube_admin'].includes(rol ?? '');

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { kullanici, yukleniyor } = useAuth();
  if (yukleniyor) return <Yukleniyor />;
  if (!kullanici) return <Navigate to="/login" />;
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { kullanici, yukleniyor } = useAuth();
  if (yukleniyor) return <Yukleniyor />;
  if (!kullanici) return <Navigate to="/login" />;
  if (kullanici.rol === 'ogrenci') return <Navigate to="/profilim" />;
  return <>{children}</>;
};

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { kullanici, yukleniyor } = useAuth();
  if (yukleniyor) return <Yukleniyor />;
  if (!kullanici) return <Navigate to="/login" />;
  if (!['admin', 'super_admin'].includes(kullanici.rol)) return <Navigate to="/" />;
  return <>{children}</>;
};

const AnaSayfa: React.FC = () => {
  const { kullanici, yukleniyor } = useAuth();
  if (yukleniyor) return <Yukleniyor />;
  if (!kullanici) return <Navigate to="/login" />;
  if (kullanici.rol === 'ogrenci') return <Navigate to="/profilim" />;
  if (kullanici.rol === 'egitmen') return <Navigate to="/egitmen" />;
  if (kullanici.rol === 'super_admin') return <Layout><SuperAdminDashboard /></Layout>;
  return <Layout><Dashboard /></Layout>;
};

const DevPanelWrapper: React.FC = () => {
  const { kullanici } = useAuth();
  if (kullanici?.rol !== 'super_admin') return null;
  return <DevPanel />;
};

const ImpersonationBanner: React.FC = () => {
  const { impersonating, geriDon } = useAuth();
  if (!impersonating) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: '#C41E3A', color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
      padding: '8px 16px', fontSize: 13, fontWeight: 500,
    }}>
      <span>👁 <strong>{impersonating}</strong> olarak görüntülüyorsunuz</span>
      <button onClick={geriDon} style={{
        background: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.4)',
        color: 'white', borderRadius: 6, padding: '4px 12px', fontSize: 12,
        cursor: 'pointer', fontWeight: 600,
      }}>
        ← Admin paneline dön
      </button>
    </div>
  );
};

const SubeGirisBanner: React.FC = () => {
  const { kullanici, subedenCik } = useAuth();
  const hasBackup = !!localStorage.getItem('sube_giren_token_backup');
  if (!hasBackup || kullanici?.rol !== 'sube_admin') return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9998,
      background: '#1a56db', color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
      padding: '8px 16px', fontSize: 13, fontWeight: 500,
    }}>
      <span>🏢 <strong>{kullanici.sube_adi}</strong> şubesi olarak yönetiyorsunuz</span>
      <button onClick={subedenCik} style={{
        background: 'rgba(255,255,255,.2)', border: '1px solid rgba(255,255,255,.4)',
        color: 'white', borderRadius: 6, padding: '4px 12px', fontSize: 12,
        cursor: 'pointer', fontWeight: 600,
      }}>
        ← Ana Panele Dön
      </button>
    </div>
  );
};

const TakvimRoute: React.FC = () => {
  const { kullanici } = useAuth();
  if (kullanici?.rol === 'egitmen') return <EgitmenLayout><Takvim /></EgitmenLayout>;
  return <Layout><Takvim /></Layout>;
};

const MesajlarRoute: React.FC = () => {
  const { kullanici } = useAuth();
  if (kullanici?.rol === 'ogrenci') return <OgrenciLayout><MesajlarSayfasi /></OgrenciLayout>;
  if (kullanici?.rol === 'egitmen') return <EgitmenLayout><MesajlarSayfasi /></EgitmenLayout>;
  return <Layout><MesajlarSayfasi /></Layout>;
};

function Yukleniyor() {
  return <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100vh', fontFamily:'sans-serif', color:'#aaa' }}>Yükleniyor...</div>;
}

const App: React.FC = () => (
  <ThemeProvider>
  <AuthProvider>
    <BrowserRouter>
      <ImpersonationBanner />
      <SubeGirisBanner />
      <DevPanelWrapper />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/gizlilik" element={<Gizlilik />} />
        <Route path="/indir" element={<Indir />} />

        <Route path="/" element={<AnaSayfa />} />

        <Route path="/profilim" element={
          <PrivateRoute><OgrenciLayout><ProfilimSayfasi /></OgrenciLayout></PrivateRoute>
        } />

        <Route path="/mesajlar" element={<PrivateRoute><MesajlarRoute /></PrivateRoute>} />

        <Route path="/egitmen" element={
          <PrivateRoute><EgitmenLayout><EgitmenPaneli /></EgitmenLayout></PrivateRoute>
        } />

        <Route path="/subeler"          element={<SuperAdminRoute><Layout><Subeler /></Layout></SuperAdminRoute>} />
        <Route path="/kullanici-yonetimi" element={<SuperAdminRoute><Layout><KullaniciYonetimi /></Layout></SuperAdminRoute>} />
        <Route path="/loglar"             element={<SuperAdminRoute><Layout><Loglar /></Layout></SuperAdminRoute>} />
        <Route path="/otomatik-postalar"  element={<SuperAdminRoute><Layout><OtomatikPostalar /></Layout></SuperAdminRoute>} />
        <Route path="/whatsapp-ai"        element={<SuperAdminRoute><Layout><WhatsappAI /></Layout></SuperAdminRoute>} />
        <Route path="/tum-kullanicilar"   element={<SuperAdminRoute><Layout><TumKullanicilar /></Layout></SuperAdminRoute>} />
        <Route path="/personel"           element={<SuperAdminRoute><Layout><Personel /></Layout></SuperAdminRoute>} />
        <Route path="/arsiv"              element={<AdminRoute><Layout><Arsiv /></Layout></AdminRoute>} />
        <Route path="/tp-ayarlari"        element={<SuperAdminRoute><Layout><TPAyarlari /></Layout></SuperAdminRoute>} />
        <Route path="/tp-listesi"         element={<SuperAdminRoute><Layout><TPListesi /></Layout></SuperAdminRoute>} />
        <Route path="/crm"         element={<AdminRoute><Layout><CRM /></Layout></AdminRoute>} />
        <Route path="/crm/:id"     element={<AdminRoute><Layout><CRMAday /></Layout></AdminRoute>} />
        <Route path="/ogrenciler"  element={<AdminRoute><Layout><Ogrenciler /></Layout></AdminRoute>} />
        <Route path="/ogrenciler/:id" element={<AdminRoute><Layout><OgrenciProfil /></Layout></AdminRoute>} />
        <Route path="/egitmenler"  element={<AdminRoute><Layout><Egitmenler /></Layout></AdminRoute>} />
        <Route path="/egitmenler/:id" element={<AdminRoute><Layout><EgitmenDetay /></Layout></AdminRoute>} />
        <Route path="/salonlar"    element={<AdminRoute><Layout><Salonlar /></Layout></AdminRoute>} />
        <Route path="/program"     element={<AdminRoute><Layout><Program /></Layout></AdminRoute>} />
        <Route path="/hakedis"     element={<AdminRoute><Layout><Hakedis /></Layout></AdminRoute>} />
        <Route path="/yoklama"     element={<AdminRoute><Layout><Yoklama /></Layout></AdminRoute>} />
        <Route path="/dersler"     element={<AdminRoute><Layout><Dersler /></Layout></AdminRoute>} />
        <Route path="/dersler/:id" element={<AdminRoute><Layout><DersDetay /></Layout></AdminRoute>} />
        <Route path="/muhasebe"    element={<AdminRoute><Layout><Muhasebe /></Layout></AdminRoute>} />
        <Route path="/kasa"        element={<AdminRoute><Layout><Kasa /></Layout></AdminRoute>} />
        <Route path="/etkinlikler" element={<AdminRoute><Layout><Etkinlikler /></Layout></AdminRoute>} />
        <Route path="/katilim"               element={<AdminRoute><Layout><Katilim /></Layout></AdminRoute>} />
        <Route path="/risk-raporu"           element={<AdminRoute><Layout><RiskRaporu /></Layout></AdminRoute>} />
        <Route path="/birlestirme-talepleri" element={<AdminRoute><Layout><BirlestirmeTalepleri /></Layout></AdminRoute>} />
        <Route path="/referans-yonetim" element={<AdminRoute><Layout><ReferansYonetim /></Layout></AdminRoute>} />
        <Route path="/kredi-paketleri" element={<AdminRoute><Layout><KrediPaketleri /></Layout></AdminRoute>} />
        <Route path="/takvim" element={
          <PrivateRoute>
            {/* egitmen → EgitmenLayout, admin → Layout */}
            <TakvimRoute />
          </PrivateRoute>
        } />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  </AuthProvider>
  </ThemeProvider>
);

export default App;
